import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [validateToken, setValidateToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const cleanEmail = email.trim().toLowerCase();
  const cleanOtp = otp.trim();

  const getErrorMessage = (err, fallback) =>
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.response?.data?.title ||
    fallback;

  const sendCode = async (e) => {
    e.preventDefault();

    if (!cleanEmail) {
      toast.error("Email adresi zorunlu.");
      return;
    }

    try {
      setLoading(true);

      await api.forgotPassword({ email: cleanEmail });

      toast.success("Kod mail adresinize gönderildi.");
      setStep(2);
    } catch (err) {
      toast.error(getErrorMessage(err, "Kod gönderilemedi."));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();

    if (!cleanOtp) {
      toast.error("Doğrulama kodu zorunlu.");
      return;
    }

    try {
      setLoading(true);

      const res = await api.validateOtp({ otp: cleanOtp });

      const token = res?.data?.validateToken;

      if (!token) {
        toast.error("Doğrulama tokenı alınamadı.");
        return;
      }

      setValidateToken(token);
      toast.success("Kod doğrulandı.");
      setStep(3);
    } catch (err) {
      toast.error(getErrorMessage(err, "Kod doğrulanamadı."));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.error("Şifre alanları zorunlu.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Şifreler eşleşmiyor.");
      return;
    }

    if (!validateToken) {
      toast.error("Doğrulama tokenı bulunamadı.");
      return;
    }

    try {
      setLoading(true);

      await api.changeForgotPassword({
        newPassword,
        validateToken,
        });

      toast.success("Şifreniz başarıyla güncellendi.");
      navigate("/login");
    } catch (err) {
      toast.error(getErrorMessage(err, "Şifre güncellenemedi."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FCFBF9] px-4 py-8 sm:px-6">
      <div className="w-full max-w-[460px] rounded-[30px] border border-zinc-200/80 bg-white p-6 shadow-[0_28px_80px_rgba(24,24,27,0.08)] sm:p-8">
        <h1 className="text-[32px] font-extrabold tracking-[-0.035em] text-zinc-950">
          Hesabınıza yeniden erişin.
        </h1>

        <p className="mb-7 mt-3 text-sm font-medium leading-6 text-zinc-500">
          {step === 1 && "Hesabınıza kayıtlı e-posta adresini girin; doğrulama kodunu gönderelim."}
          {step === 2 && "E-postanıza gelen doğrulama kodunu girin."}
          {step === 3 && "Yeni şifrenizi belirleyin ve hesabınıza güvenle dönün."}
        </p>

        {step === 1 && (
          <form onSubmit={sendCode} className="space-y-4">
            <Input
              type="email"
              placeholder="ornek@isletmeniz.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200"
            />

            <Button type="submit" className="h-12 w-full rounded-full bg-[#EA580C] text-sm font-bold text-white shadow-[0_14px_32px_rgba(234,88,12,0.22)] hover:bg-[#C2410C]" disabled={loading}>
              {loading ? "Gönderiliyor..." : "Doğrulama Kodu Gönder"}
            </Button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={verifyCode} className="space-y-4">
            <Input
              type="text"
              inputMode="numeric"
              placeholder="Doğrulama kodu"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
              className="h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200 text-center tracking-[0.24em]"
            />

            <Button type="submit" className="h-12 w-full rounded-full bg-[#EA580C] text-sm font-bold text-white shadow-[0_14px_32px_rgba(234,88,12,0.22)] hover:bg-[#C2410C]" disabled={loading}>
              {loading ? "Kontrol ediliyor..." : "Kodu Doğrula"}
            </Button>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={resetPassword} className="space-y-4">
            <Input
              type="password"
              placeholder="Yeni şifreniz"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className="h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200"
            />

            <Input
              type="password"
              placeholder="Yeni şifrenizi tekrar yazın"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 focus-visible:ring-orange-200"
            />

            <Button type="submit" className="h-12 w-full rounded-full bg-[#EA580C] text-sm font-bold text-white shadow-[0_14px_32px_rgba(234,88,12,0.22)] hover:bg-[#C2410C]" disabled={loading}>
              {loading ? "Güncelleniyor..." : "Şifreyi Güncelle"}
            </Button>
          </form>
        )}

        <div className="mt-7 border-t border-zinc-100 pt-6 text-center">
          <Link
            to="/login"
            className="text-sm font-bold text-zinc-600 transition hover:text-[#EA580C]"
          >
            Giriş ekranına dön
          </Link>
        </div>
      </div>
    </div>
  );
}